/**
 * Classify why a game failed to load, so the screen shows the RIGHT message.
 *
 * The cardinal rule: a network/transient failure must NOT be reported as
 * "Game not found" (the game is probably fine — the phone just can't reach the
 * server). Only a real 404 means the game is gone.
 *
 *   'ai-takeover' → HTTP 403 with code AI_TAKEOVER: the viewer's seat was handed to an
 *                  AI after their time expired; show the "time expired" screen
 *   'forbidden'  → HTTP 403: private game the viewer isn't invited to (PIN entry)
 *   'notfound'   → HTTP 404, or no error but no game: the game genuinely isn't there
 *   'connection' → network failure / timeout / 5xx / other: "check your connection"
 *
 * Pure (no RN deps) so it's unit-testable. HttpError carries a numeric status;
 * a thrown network/timeout error is a plain Error (no status) → 'connection'.
 */
import { HttpError } from './api'

export type GameLoadErrorKind = 'ai-takeover' | 'forbidden' | 'notfound' | 'connection'

export function classifyGameLoadError(error: unknown): GameLoadErrorKind {
  if (error instanceof HttpError) {
    if (error.status === 403 && error.code === 'AI_TAKEOVER') return 'ai-takeover'
    if (error.status === 403) return 'forbidden'
    if (error.status === 404) return 'notfound'
    // 5xx, 401, 408, etc. — transient/unexpected. Never claim "not found"
    // for these; they're connection-class problems from the user's view.
    return 'connection'
  }
  // A non-HttpError with a value means fetch threw — network down / timeout.
  if (error) return 'connection'
  // No error at all but the screen still has no game: genuinely not found.
  return 'notfound'
}

/**
 * Poll interval (ms) for the game-detail query. Pure so it's testable.
 *
 *   • WebSocket live         → false (state is pushed; polling is redundant).
 *   • 403/404 error state    → false — these are DEFINITIVE: a forbidden or
 *     missing game won't start returning 200 on its own (it needs a redeem or
 *     navigation), so polling every 15s just hammers the endpoint for nothing.
 *   • otherwise (disconnected) → 15s fallback poll.
 */
export function gameRefetchInterval(error: unknown, wsConnected: boolean): number | false {
  // Only suppress on an ACTUAL 403/404 error. A null/absent error means a
  // healthy load (data present) — classifyGameLoadError maps null → 'notfound',
  // so guard on `error` being truthy first, or we'd wrongly stop polling a
  // perfectly good game.
  if (error) {
    const kind = classifyGameLoadError(error)
    if (kind === 'ai-takeover' || kind === 'forbidden' || kind === 'notfound') return false
  }
  return wsConnected ? false : 15_000
}

/**
 * Whether to auto-redeem an invite code the viewer arrived with (via
 * /invite/<code>). True only when we have a well-formed 4-digit code AND the
 * game is currently blocked by a 403 (private, viewer not yet granted access) —
 * redeeming grants a pending invitation so the lobby loads without the viewer
 * retyping the code they came in with. Pure so it's testable; the caller still
 * guards single-fire with a ref.
 */
export function shouldAutoRedeemInvite(
  inviteCode: string | undefined | null,
  error: unknown,
): boolean {
  if (!inviteCode || inviteCode.length !== 4) return false
  return classifyGameLoadError(error) === 'forbidden'
}
